import {
  chapterTimeZone,
  eventChapterOf,
  findEventEntry,
} from "@repo/vihangam-yog-shared";
import * as Sentry from "@sentry/react";
import { ArrowLeft, ArrowLeftRight, ChevronDown } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { CHAPTER } from "../../chapter";
import {
  eventsTodayFromData,
  groupEventsForPicker,
  isEventToday,
} from "../../lib/eventTiming";
import { friendlyError } from "../../lib/friendlyError";
import { trpc } from "../../trpc";
import { isMarkedNotComing } from "../event-registrations/notComing";
import {
  type EventGroup,
  type Registration,
} from "../event-registrations/shared";

import { GuestCard, type GuestCardState } from "./GuestCard";
import { ReceptionView } from "./ReceptionView";
import { ScanOverlay } from "./ScanOverlay";
import { personFromParams, REF_RE, type RefMatch } from "./matchRef";
import { createRefOcr, type RefOcr } from "./ocr";

/** A valid-shaped placeholder so a disabled lookup still has an input. */
const NO_REF = `${CHAPTER.refPrefix}-DEMO-PASS`;

/** What the door is told when a check-in never reached us. */
const CHECK_IN_OFFLINE =
  "That didn't save. Check your signal and tap Check in again.";
const UNDO_OFFLINE = "That didn't save. Check your signal and try again.";

function slugFromPath(): string | null {
  const seg = window.location.pathname.split("/").filter(Boolean);
  if (seg[0]?.toLowerCase() !== "reception" || !seg[1]) {
    return null;
  }
  try {
    return decodeURIComponent(seg[1]);
  } catch {
    return null;
  }
}

/** A pass deep-linked into the admin: the reference and, for one
 *  person's own pass, their position in the party. */
interface PendingPass {
  ref: string;
  person: number | null;
}

/** The ?ref= (and ?p=) of a scanned pass; only ever a well-formed reference. */
function refFromQuery(): PendingPass | null {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref")?.trim().toUpperCase() ?? "";
  return REF_RE.test(ref) ? { ref, person: personFromParams(params) } : null;
}

function receptionPath(slug: string, ref?: string | null): string {
  return `/reception/${encodeURIComponent(slug)}${ref ? `?ref=${ref}` : ""}`;
}

/** "Sat 19 Sept" in the event's own time zone, or the catalogue's words
 *  ("Every Sunday") for an event with no date. */
function shortDateOf(g: EventGroup): string {
  const date = g.eventStartsAt ? new Date(g.eventStartsAt) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return g.eventDateLabel;
  }
  const entry = findEventEntry(g.eventSlug);
  return date.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: entry ? chapterTimeZone(eventChapterOf(entry)) : CHAPTER.timezone,
  });
}

/** The city, when the event belongs to one ("Online & anywhere" and
 *  weekly events have none worth showing). */
function cityOf(g: EventGroup): string | null {
  return g.eventLocation ? g.eventLocationLabel : null;
}

/**
 * Reception: the door, on its own page. Opens on today's event (judged
 * from the guest list the backend sends, so Christchurch still opens in
 * the Australian build), or a picker. Scan reads a QR pass or the
 * printed reference number and resolves it against this event's guest
 * list; a hit, a tapped row and a deep-linked pass all land in the same
 * guest card with one big Check in. The OCR worker is warmed on mount.
 */
export function Reception() {
  const query = trpc.adminListEventRegistrations.useQuery(undefined, {
    retry: false,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
  const utils = trpc.useUtils();
  const checkIn = trpc.adminCheckInRegistration.useMutation();

  const [slug, setSlug] = useState<string | null>(() => slugFromPath());
  const [pending, setPending] = useState<PendingPass | null>(() =>
    refFromQuery(),
  );
  const pendingRef = pending?.ref ?? null;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [card, setCard] = useState<GuestCardState | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const [justDone, setJustDone] = useState(false);
  const [ocr, setOcr] = useState<RefOcr | null>(null);
  /** Set when the text reader failed to load; the overlay says so. */
  const [ocrError, setOcrError] = useState<string | null>(null);
  const doneTimer = useRef<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const hasData = query.data !== undefined;
  const events = useMemo(() => query.data?.events ?? [], [query.data]);
  // "Now" is when the list last arrived: it refreshes every 10 seconds,
  // so a tablet left on overnight moves on to the next day by itself.
  const dataTime = query.dataUpdatedAt;
  const today = useMemo(
    () => eventsTodayFromData(events, new Date(dataTime)),
    [events, dataTime],
  );
  const group: EventGroup | null = useMemo(
    () => events.find((g) => g.eventSlug === slug) ?? null,
    [events, slug],
  );

  // No event in the URL and no pass to resolve: today's single event, or
  // the picker. Wait for the list: before it arrives there is no today,
  // and opening the picker then would leave it covering today's event.
  useEffect(() => {
    if (slug || pendingRef || !hasData) {
      return;
    }
    const only = today[0];
    if (today.length === 1 && only) {
      setSlug(only.eventSlug);
      setPickerOpen(false);
    } else {
      setPickerOpen(true);
    }
  }, [slug, pendingRef, hasData, today]);

  // Keep the URL on the selected event (path only; ?ref stays until used).
  useEffect(() => {
    if (!slug) {
      return;
    }
    const target = receptionPath(slug, pendingRef);
    if (window.location.pathname + window.location.search !== target) {
      window.history.replaceState({}, "", target);
    }
  }, [slug, pendingRef]);

  // Back/forward, or another screen pushing /reception/<slug> while we're
  // mounted.
  useEffect(() => {
    const onPop = () => {
      const next = slugFromPath();
      if (next) {
        setSlug(next);
      }
      const pass = refFromQuery();
      if (pass) {
        setPending(pass);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Warm the text reader while the volunteer is still on Wi-Fi.
  useEffect(() => {
    let alive = true;
    let instance: RefOcr | null = null;
    createRefOcr()
      .then((o) => {
        if (!alive) {
          void o.terminate();
          return;
        }
        instance = o;
        setOcr(o);
      })
      .catch((err: Error) => {
        if (!alive) {
          return;
        }
        setOcrError(err.message || "could not load");
        Sentry.captureException(err, { tags: { area: "reception-ocr-load" } });
      });
    return () => {
      alive = false;
      void instance?.terminate();
    };
  }, []);

  useEffect(
    () => () => {
      if (doneTimer.current !== null) {
        window.clearTimeout(doneTimer.current);
      }
    },
    [],
  );

  // ── Resolve a reference that isn't on this event's list (a deep-linked
  // pass, or a scan of another event's pass). Same authed lookup for both.
  const [lookupRef, setLookupRef] = useState<string | null>(null);
  const lookup = trpc.adminTicketByRef.useQuery(
    { confirmationRef: lookupRef ?? NO_REF },
    { enabled: lookupRef !== null, retry: false },
  );
  useEffect(() => {
    if (pendingRef && lookupRef !== pendingRef) {
      setLookupRef(pendingRef);
    }
  }, [pendingRef, lookupRef]);
  useEffect(() => {
    if (!lookupRef || lookup.isLoading) {
      return;
    }
    const ticket = lookup.data;
    const fromDeepLink = pendingRef === lookupRef;
    if (lookup.isError || !ticket) {
      // The lookup returns null for an unknown reference; an error means
      // the request itself failed, which the card words differently.
      setCard({ kind: "not-found", ref: lookupRef, offline: lookup.isError });
      setLookupRef(null);
      if (fromDeepLink) {
        setPending(null);
      }
      return;
    }
    if (fromDeepLink) {
      // A scanned pass opens its own event, then its guest card. A link
      // that names its event (the guest list's Check in) stays on that
      // event when its list holds the reference: Singapore files both
      // days of a combined booking under one reference, and the lookup
      // returns the first day.
      if (slug && !hasData) {
        return;
      }
      const named = events.find((e) => e.eventSlug === slug);
      const targetSlug =
        named?.registrations.some(
          (x) => x.confirmationRef === ticket.confirmationRef,
        ) === true
          ? named.eventSlug
          : ticket.eventSlug;
      if (targetSlug !== slug) {
        setSlug(targetSlug);
        setPickerOpen(false);
      }
      const g = events.find((e) => e.eventSlug === targetSlug);
      const r = g?.registrations.find(
        (x) => x.confirmationRef === ticket.confirmationRef,
      );
      if (!r) {
        return;
      } // the list for that event is still loading
      setCardError(null);
      setJustDone(false);
      setCard({
        kind: "guest",
        registration: r,
        person: pending?.person ?? null,
      });
      setPending(null);
      setLookupRef(null);
      window.history.replaceState({}, "", receptionPath(targetSlug));
      return;
    }
    const entry = findEventEntry(ticket.eventSlug);
    setCard({
      kind: "wrong-event",
      name: ticket.name,
      confirmationRef: ticket.confirmationRef,
      eventSlug: ticket.eventSlug,
      eventTitle: entry?.title ?? ticket.eventSlug,
      eventDateLabel: entry?.dateLabel ?? "",
      notComing: isMarkedNotComing(ticket),
    });
    setLookupRef(null);
  }, [
    lookupRef,
    lookup.data,
    lookup.isLoading,
    lookup.isError,
    pendingRef,
    pending?.person,
    events,
    hasData,
    slug,
  ]);

  const knownRefs = useMemo(
    () => (group?.registrations ?? []).map((r) => r.confirmationRef),
    [group],
  );

  const onRead = useCallback(
    (m: RefMatch, raw: string, person: number | null) => {
      if (!group) {
        return;
      }
      if (m.kind === "exact" || m.kind === "fuzzy") {
        const r = group.registrations.find((x) => x.confirmationRef === m.ref);
        if (r) {
          setCardError(null);
          setJustDone(false);
          setCard({ kind: "guest", registration: r, person });
        }
        return;
      }
      if (m.kind === "ambiguous") {
        setCard({
          kind: "ambiguous",
          registrations: group.registrations.filter((x) =>
            m.refs.includes(x.confirmationRef),
          ),
        });
        return;
      }
      // Nothing on this list: chase only a well-formed reference (another
      // event's pass reads cleanly; noise does not).
      if (REF_RE.test(raw)) {
        setLookupRef(raw);
      }
    },
    [group],
  );

  /**
   * Record an arrival (or undo one): the whole registration, or one
   * person of its party. The big action closes the card once it lands;
   * a tapped party row keeps it open so the next name can be ticked.
   */
  const setPresent = (
    r: Registration,
    checkedIn: boolean,
    person?: number,
    stayOpen = false,
  ) => {
    setCardError(null);
    checkIn.mutate(
      { id: r.id, checkedIn, person },
      {
        onSuccess: () => {
          void utils.adminListEventRegistrations.invalidate();
          if (stayOpen) {
            return;
          }
          if (checkedIn) {
            setJustDone(true);
            doneTimer.current = window.setTimeout(() => {
              setJustDone(false);
              setCard(null);
            }, 1200);
          } else {
            setCard(null);
          }
        },
        onError: (err) =>
          setCardError(
            friendlyError(err, checkedIn ? CHECK_IN_OFFLINE : UNDO_OFFLINE),
          ),
      },
    );
  };

  // The card holds a snapshot; a party's ticks land on the live list, so
  // read the registration back from it while the card is open.
  const liveCard: GuestCardState | null = useMemo(() => {
    if (!card || card.kind !== "guest") {
      return card;
    }
    const fresh = group?.registrations.find(
      (x) => x.id === card.registration.id,
    );
    return fresh ? { ...card, registration: fresh } : card;
  }, [card, group]);

  const closeCard = () => {
    if (doneTimer.current !== null) {
      window.clearTimeout(doneTimer.current);
      doneTimer.current = null;
    }
    setJustDone(false);
    setCard(null);
  };

  const openEvent = (next: string) => {
    setCard(null);
    setScanning(false);
    setWalkInOpen(false);
    setSlug(next);
    setPickerOpen(false);
  };

  const pickerNow = new Date(dataTime);
  const picked = groupEventsForPicker(events, pickerNow);
  const nothingAhead =
    picked.today.length + picked.comingUp.length + picked.everyWeek.length ===
    0;

  const pickerRow = (g: EventGroup) => {
    const place = cityOf(g);
    const showArrivals = isEventToday(g, pickerNow) || g.checkedInPeople > 0;
    return (
      <li key={g.eventSlug}>
        <button
          type="button"
          onClick={() => openEvent(g.eventSlug)}
          className="flex min-h-16 w-full items-center justify-between gap-3 rounded-xl border border-[color:var(--color-ink)]/20 bg-[color:var(--color-paper)] px-4 py-2.5 text-left transition-colors hover:border-[color:var(--color-amber)]"
        >
          <span className="min-w-0">
            <span className="block text-[16px] leading-snug font-semibold break-words text-[color:var(--color-ink)]">
              {g.eventTitle}
            </span>
            <span className="mt-0.5 block text-[14px] text-[color:var(--color-ink-soft)]">
              {[shortDateOf(g), place].filter(Boolean).join(" · ")}
            </span>
          </span>
          <span className="shrink-0 text-right text-[14px] text-[color:var(--color-ink-soft)] tabular-nums">
            {showArrivals
              ? `${g.checkedInPeople} of ${g.totalPeople} here`
              : g.totalPeople === 0
                ? "No bookings yet"
                : `${g.totalPeople} ${g.totalPeople === 1 ? "person" : "people"}`}
          </span>
        </button>
      </li>
    );
  };

  const pickerSection = (title: string, list: EventGroup[]) =>
    list.length > 0 ? (
      <section className="mt-6">
        <h2 className="text-[15px] font-semibold text-[color:var(--color-ink)]">
          {title}
        </h2>
        <ul className="mt-2 space-y-2">{list.map(pickerRow)}</ul>
      </section>
    ) : null;

  const pickerBody = (
    <div>
      {group && pickerOpen ? (
        <button
          type="button"
          onClick={() => setPickerOpen(false)}
          className="mb-3 inline-flex min-h-11 items-center gap-2 text-left text-[15px] font-semibold text-[color:var(--color-ink)]"
        >
          <ArrowLeft size={18} aria-hidden="true" className="shrink-0" /> Back
          to {group.eventTitle}
        </button>
      ) : null}
      <h1 className="text-[22px] leading-snug font-bold text-[color:var(--color-ink)] md:text-[26px]">
        Choose an event
      </h1>
      {pickerSection("Today", picked.today)}
      {pickerSection("Coming up", picked.comingUp)}
      {pickerSection("Every week", picked.everyWeek)}
      {nothingAhead && !query.error ? (
        <p className="mt-4 text-[16px] text-[color:var(--color-ink-soft)]">
          {picked.finished.length > 0
            ? "No upcoming events. Open a finished one below."
            : "There are no events to show yet."}
        </p>
      ) : null}
      {picked.finished.length > 0 ? (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowFinished((v) => !v)}
            aria-expanded={showFinished}
            className="inline-flex min-h-11 items-center gap-2 text-[15px] font-semibold text-[color:var(--color-ink)]"
          >
            <ChevronDown
              size={18}
              className={`transition-transform ${showFinished ? "" : "-rotate-90"}`}
              aria-hidden="true"
            />
            {showFinished
              ? "Hide finished events"
              : `Show finished events (${picked.finished.length})`}
          </button>
          {showFinished ? (
            <ul className="mt-2 space-y-2">{picked.finished.map(pickerRow)}</ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const eventMeta = group
    ? [group.eventDateLabel, cityOf(group)].filter(Boolean).join(" · ")
    : "";

  // Today's only event is about to open: don't flash the picker first.
  const autoOpening = !slug && !pendingRef && hasData && today.length === 1;
  let body: ReactNode;
  if (pendingRef && !group) {
    body = (
      <p className="py-8 text-[16px] text-[color:var(--color-ink-soft)]">
        Opening the pass…
      </p>
    );
  } else if (!group && (query.isLoading || autoOpening)) {
    body = (
      <p className="py-8 text-[16px] text-[color:var(--color-ink-soft)]">
        Loading events…
      </p>
    );
  } else if (!group || pickerOpen) {
    body = pickerBody;
  } else {
    body = (
      <>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-[22px] leading-snug font-bold break-words text-[color:var(--color-ink)] md:text-[26px]">
              {group.eventTitle}
            </h1>
            {eventMeta ? (
              <p className="mt-1 text-[15px] text-[color:var(--color-ink-soft)]">
                {eventMeta}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex min-h-11 shrink-0 items-center gap-2 self-start rounded-full border border-[color:var(--color-line-strong)] px-4 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
          >
            <ArrowLeftRight size={16} aria-hidden="true" /> Change event
          </button>
        </div>

        <ReceptionView
          key={group.eventSlug}
          group={group}
          onScan={() => setScanning(true)}
          walkInOpen={walkInOpen}
          onWalkInOpenChange={setWalkInOpen}
          searchInputRef={searchInputRef}
          onOpenGuest={(r) => {
            setCardError(null);
            setJustDone(false);
            setCard({ kind: "guest", registration: r });
          }}
        />
      </>
    );
  }

  return (
    <section className="mx-auto max-w-[1100px] px-4 py-5 md:px-10 md:py-10">
      {query.error ? (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-700/40 bg-red-700/[0.07] px-4 py-3 text-[15px] font-medium text-red-700"
        >
          Couldn&apos;t load the guest list.{" "}
          {friendlyError(
            query.error,
            "Check your internet connection. It will keep trying on its own.",
          )}
        </p>
      ) : null}

      {body}

      {scanning && group ? (
        <ScanOverlay
          knownRefs={knownRefs}
          ocr={ocr}
          ocrError={ocrError}
          onRead={onRead}
          onClose={() => setScanning(false)}
        />
      ) : null}

      {liveCard ? (
        <GuestCard
          state={liveCard}
          busy={checkIn.isPending}
          error={cardError}
          justDone={justDone}
          onCheckIn={(r, person) => setPresent(r, true, person)}
          onUndo={(r, person) => setPresent(r, false, person)}
          onTogglePerson={(r, person, checkedIn) =>
            setPresent(r, checkedIn, person, true)
          }
          onPick={(r) => {
            setCardError(null);
            setCard({ kind: "guest", registration: r });
          }}
          onOpenEvent={openEvent}
          onSearch={() => {
            // Focus first, inside the tap, so a phone raises its keyboard.
            searchInputRef.current?.focus();
            setScanning(false);
            closeCard();
          }}
          onAddWalkIn={() => {
            setScanning(false);
            closeCard();
            setWalkInOpen(true);
          }}
          onClose={closeCard}
        />
      ) : null}
    </section>
  );
}
